/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { simpleFetch } from "../../../restWrapper";
import { renderErrorMsg } from "../../../assets/component/renderErrorMsg.js";
import PropTypes from "prop-types";
import UpgradeNoticeCard from "./UpgradeNoticeCard";
import DonorSearchPicker from "./DonorSearchPicker";
import ProjectSearchPicker from "./ProjectSearchPicker";

class RenderReports extends Component {
  constructor(props) {
    super(props);
    let open = new Date();
    open.setHours(0, 0, 0, 0);
    let end = new Date();
    end.setHours(23, 59, 59, 59);
    this.state = {
      reportType: "transaction",
      startDate: open,
      endDate: end,
      msg: "",
      paymentModes: ["manual", "automatic", "payout"],
      paymentModesOpen: false,
      selectedDonors: [],
      selectedProjects: [],
    };
  }
  render() {
    const languageObj = this.props.languageObj;
    const currentPlan = this.props.currentPlan ? this.props.currentPlan.toLowerCase() : '';
    const redirectToOrgBilling = this.props.redirectToOrgBilling;

    const textObj = languageObj.textObj;
    const convertDateToText = (d) => {
      const convertD = new Date(d);
      const theDate = [
        convertD.getDate(),
        convertD.getMonth() + 1,
        convertD.getFullYear(),
      ];
      const itemDate = theDate[2] + "/" + theDate[1] + "/" + theDate[0];
      return itemDate;
    };

    const submitSummary = () => {
      this.setState({ msg: languageObj.summarySentMsg || textObj[1] });
      // POST for the same reason as the transaction report above (CP-241).
      const query = {};
      if (this.state.selectedProjects && this.state.selectedProjects.length > 0) {
        query.projectIds = this.state.selectedProjects.map((c) => c.key);
      }
      simpleFetch("/organizations/projectSummary", "POST", query).then((res) => {
        let msg = "";
        switch (res.status) {
          case 200:
          case 204:
            msg = languageObj.summarySentMsg || textObj[1];
            break;
          default:
            msg = textObj[2];
        }
        this.setState({
          msg: msg,
        });
      });
    };

    const submitTransaction = () => {
      if (!this.state.startDate) return false;
      const moment = require("moment");
      const startDate = moment(this.state.startDate).startOf("day").toISOString(false);
      const endDate = (this.state.endDate
        ? moment(this.state.endDate)
        : moment(this.state.startDate)
      ).endOf("day").toISOString(false);
      this.setState({
        msg: textObj[1],
      });
      // POST with the params in the body (CP-241): a large project/donor
      // selection overflows a GET query string — production's WAF blocks
      // query strings over 2KB, so a few hundred selected ids never even
      // reached the backend.
      const query = {
        startDate: startDate,
        endDate: endDate,
        paymentModes: this.state.paymentModes,
      };
      if (this.state.selectedDonors && this.state.selectedDonors.length > 0) {
        query.donorKeys = this.state.selectedDonors.map((d) => d.key);
      }
      if (this.state.selectedProjects && this.state.selectedProjects.length > 0) {
        query.projectIds = this.state.selectedProjects.map((c) => c.key);
      }
      simpleFetch("/organizations/listTransactions", "POST", query).then((res) => {
        let msg = "";
        switch (res.status) {
          case 200:
            msg =
              textObj[0][0] +
              convertDateToText(startDate) +
              textObj[0][1] +
              convertDateToText(endDate);
            break;
          case 204:
            msg = textObj[1];
            break;
          default:
            msg = textObj[2];
        }
        this.setState({
          msg: msg,
        });
      });
    };

    const submitBtn = () => {
      if (this.state.reportType === "summary") {
        submitSummary();
      } else {
        submitTransaction();
      }
    };

    return (
      <div className="OrgBalanceReports-content">
        <div className="OrgBalanceReports-contentContainer">
          <div className="OrgBalanceReports-contentHeader">
            <div className="OrgBalanceReports-info">
              {languageObj.contentHeader}
            </div>
          </div>

          {currentPlan === "premium" || currentPlan === "pro" || currentPlan === "plus" ? (
            <div className="OrgBalanceReports-contentContainer">
              <div className="OrgBalanceReports-heading">{languageObj.heading}</div>
              <div className="OrgBalanceReports-subheading">
                {this.state.reportType === "summary"
                  ? (languageObj.summarySubheading || languageObj.subheading)
                  : languageObj.subheading}
              </div>

              <div className="OrgBalanceReports-reportTypeToggle">
                <button
                  type="button"
                  className={
                    "OrgBalanceReports-reportTypeBtn" +
                    (this.state.reportType === "transaction" ? " OrgBalanceReports-reportTypeBtn--active" : "")
                  }
                  onClick={() => this.setState({ reportType: "transaction", msg: "" })}
                >
                  {languageObj.reportTypeTransaction || "Transaction Export"}
                </button>
                <button
                  type="button"
                  className={
                    "OrgBalanceReports-reportTypeBtn" +
                    (this.state.reportType === "summary" ? " OrgBalanceReports-reportTypeBtn--active" : "")
                  }
                  onClick={() => this.setState({ reportType: "summary", msg: "" })}
                >
                  {languageObj.reportTypeSummary || "Project Summary"}
                </button>
              </div>

              {this.state.reportType === "transaction" && (() => {
                const f = (languageObj && languageObj.filters) || {};
                const modeLabels = {
                  manual: f.modeManual || "Manual",
                  automatic: f.modeAutomatic || "Automatic",
                  payout: f.modePayout || "Payouts",
                };
                const triggerLabel = (() => {
                  const m = this.state.paymentModes;
                  if (m.length === 3) return f.modeAll || "All payments";
                  if (m.length === 0) return f.modeNone || "(none selected)";
                  return m.map((k) => modeLabels[k]).join(" + ");
                })();
                const isPremium = currentPlan === "premium";
                return (
                  <div className="OrgBalanceReports-modePicker">
                    <div
                      className="OrgBalanceReports-modePickerTrigger"
                      onClick={() => {
                        if (isPremium) this.setState({ paymentModesOpen: !this.state.paymentModesOpen });
                      }}
                      style={{
                        cursor: isPremium ? "pointer" : "not-allowed",
                        opacity: isPremium ? 1 : 0.6,
                      }}
                      title={isPremium ? undefined : (f.modeLockTooltip || "Upgrade to Premium to filter by payment type")}
                    >
                      <span className="OrgBalanceReports-modePickerLabel">{triggerLabel}</span>
                      {!isPremium && (
                        <span className="OrgBalanceReports-modePickerBadge">{f.premiumBadge || "Premium"}</span>
                      )}
                      <span className="OrgBalanceReports-modePickerCaret">▾</span>
                    </div>
                    {this.state.paymentModesOpen && isPremium && (
                      <React.Fragment>
                        <div
                          className="OrgBalanceReports-modePickerOverlay"
                          onClick={() => this.setState({ paymentModesOpen: false })}
                        />
                        <div className="OrgBalanceReports-modePickerDropdown">
                          {["manual", "automatic", "payout"].map((mode) => (
                            <label key={mode} className="OrgBalanceReports-modeOption">
                              <input
                                type="checkbox"
                                checked={this.state.paymentModes.includes(mode)}
                                onChange={(e) => {
                                  const newModes = e.target.checked
                                    ? [...this.state.paymentModes, mode]
                                    : this.state.paymentModes.filter((m) => m !== mode);
                                  this.setState({ paymentModes: newModes, msg: "" });
                                }}
                              />
                              <span>{modeLabels[mode]}</span>
                            </label>
                          ))}
                        </div>
                      </React.Fragment>
                    )}
                  </div>
                );
              })()}

              {this.state.reportType === "transaction" && (
                <div className="OrgBalanceReports-inputFieldContainer">
                  <div className="OrgBalanceReports-inputField">
                    <DatePicker
                      selectsRange
                      startDate={this.state.startDate}
                      endDate={this.state.endDate}
                      maxDate={new Date()}
                      className="OrgBalanceReports-datePicker"
                      onChange={(update) => {
                        const [start, end] = update;
                        this.setState({
                          startDate: start,
                          endDate: end,
                          msg: "",
                        });
                      }}
                      dateFormat="yyyy/MM/dd"
                      isClearable
                      placeholderText={(languageObj && languageObj.filters && languageObj.filters.datePlaceholder) || "Pick a date or date range"}
                    />
                  </div>
                </div>
              )}

              {this.state.reportType === "transaction" && (
                <div className="OrgBalanceReports-donorPickerWrap">
                  <DonorSearchPicker
                    selected={this.state.selectedDonors}
                    onChange={(selected) => this.setState({ selectedDonors: selected, msg: "" })}
                    disabled={currentPlan !== "premium"}
                    languageObj={(languageObj && languageObj.filters) || {}}
                  />
                  {currentPlan !== "premium" && (
                    <span className="OrgBalanceReports-premiumBadge">
                      {(languageObj && languageObj.filters && languageObj.filters.premiumBadge) || "Premium"}
                    </span>
                  )}
                </div>
              )}

              <div className="OrgBalanceReports-projectPickerWrap">
                <ProjectSearchPicker
                  selected={this.state.selectedProjects}
                  onChange={(selected) => this.setState({ selectedProjects: selected, msg: "" })}
                  disabled={currentPlan !== "premium"}
                  languageObj={(languageObj && languageObj.filters) || {}}
                  subDomain={this.props.subDomain}
                  language={this.props.language}
                />
                {currentPlan !== "premium" && (
                  <span className="OrgBalanceReports-premiumBadge">
                    {(languageObj && languageObj.filters && languageObj.filters.premiumBadge) || "Premium"}
                  </span>
                )}
              </div>

              <div className="OrgBalanceReports-msg">
                {renderErrorMsg(this.state.msg)}
              </div>
    
              <div onClick={submitBtn} className="OrgBalanceReports-submitBtn">
                {languageObj.submitBtn}
              </div>
            </div>
          ) : (
            <UpgradeNoticeCard
              heading="Unlock Balance Reports"
              body="Balance reports are available on the Plus plan and above. Upgrade to export transaction summaries for your organisation."
              onUpgradeClick={redirectToOrgBilling}
            />
          )}

          <div className="OrgBalanceReports-footer" />
        </div>
      </div>
    );
  }
}

RenderReports.propTypes = {
  languageObj: PropTypes.object,
  currentPlan: PropTypes.string,
  redirectToOrgBilling: PropTypes.func,
  subDomain: PropTypes.string,
  language: PropTypes.string,
};

export default RenderReports;
